import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { QuoteDrawerService } from '../../quote-builder/services/quote-drawer.service';
import { MatIcon } from '@angular/material/icon';
import { AppButtons } from '../../../shared/app-buttons/app-buttons';
import { getAuditTrailActionBadgeClass, getAuditTrailTimestampParts, normalizeAuditTrailActionType, normalizeAuditTrailActorRole, normalizeAuditTrailScreenName } from '../audit-trail-display.util';
import { AuditDetails } from '../audits.constant';
import { MatDialog } from '@angular/material/dialog';
import { LeadDetailView } from '../../lead-detail-view/lead-detail-view';
import { AppReviewApiService } from '../../application-review/services/app-review-api.service';

/** Maximum number of characters before a field value is truncated. */
const MAX_FIELD_LENGTH = 200;
/** Actor roles that identify a system-generated event. */
const SYSTEM_ROLES = new Set(['System']);
/** Action types that identify a system-generated or destructive event. */
const SYSTEM_ACTION_TYPES = new Set(['SystemUpdate', 'Delete']);

@Component({
  selector: 'app-audit-details-panel',
  imports: [CommonModule, MatIcon, AppButtons],
  templateUrl: './audit-details-panel.html',
  styleUrl: './audit-details-panel.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuditDetailsPanel {
  close = output<void>();
  auditDetails = input<AuditDetails | null>(null);

  private readonly dialog = inject(MatDialog);
  private readonly drawerService = inject(QuoteDrawerService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly appReviewApiService = inject(AppReviewApiService);

  /** Tracks which long-value field keys the user has expanded via "Show more". */
  readonly expandedFields = signal<Set<string>>(new Set());

  readonly panelData = computed(() => {
    const details = this.auditDetails();
    const timestampParts = getAuditTrailTimestampParts(details?.timestamp ?? '');

    return {
      entityId: this.toDisplayText(details?.entityId),
      actionLabel: this.toDisplayText(details?.actionType ? normalizeAuditTrailActionType(details.actionType) : null),
      actionClass: details?.actionType ? getAuditTrailActionBadgeClass(details.actionType) : '',
      entityType: this.toDisplayText(details?.entityType ?? 'Lead'),
      entityOwner: this.toDisplayText(details?.clientName),
      actorName: this.toDisplayText(details?.modifiedBy || details?.modifiedByUserId),
      actorRole: this.toDisplayText(details?.actorRole ? normalizeAuditTrailActorRole(details.actorRole) : null),
      timeframe: timestampParts ? `${timestampParts.date}, ${timestampParts.time}` : '-',
      ipAddress: this.toDisplayText(details?.ipAddress),
      screen: this.toDisplayText(details?.screenName ? normalizeAuditTrailScreenName(details.screenName) : null),
      attribute: this.toDisplayText(details?.fieldName),
      before: this.toDisplayText(details?.oldValue),
      after: this.toDisplayText(details?.newValue),
      reason: this.toDisplayText(details?.reason),
      inactivityPeriod: this.toDisplayDays(details?.inactivityPeriodDays),
      thresholdApplied: this.toDisplayDays(details?.thresholdAppliedDays),
      ruleReference: this.toDisplayText(details?.ruleReference),
      policyReference: this.toDisplayText(details?.policyReference),
      triggerCondition: this.toDisplayText(details?.triggerCondition),
      isSensitive: details?.isSensitive ? 'Yes' : 'No',
    };
  });

  /**
   * Zone D visibility: Additional Details section is shown only for system-triggered
   * or rule-triggered events (System actor role, SystemUpdate/Delete action, or any
   * conditional fields populated in the API response).
   */
  readonly isSystemTriggered = computed(() => {
    const details = this.auditDetails();
    if (!details) return false;

    const isSystemRole = SYSTEM_ROLES.has(details.actorRole ?? '');
    const isSystemAction = SYSTEM_ACTION_TYPES.has(details.actionType ?? '');
    const hasAdditionalData = !!(
      details.reason ||
      details.ruleReference ||
      details.triggerCondition ||
      details.policyReference ||
      typeof details.inactivityPeriodDays === 'number' ||
      typeof details.thresholdAppliedDays === 'number'
    );

    return isSystemRole || isSystemAction || hasAdditionalData;
  });

  /**
   * Entity unavailable: both entityType and clientName are absent.
   * Used to show a degraded read-only state with CTA disabled.
   */
  readonly isEntityUnavailable = computed(() => {
    const details = this.auditDetails();
    if (!details) return false;
    const hasType = (details.entityType ?? '').trim().length > 0;
    const hasClient = (details.clientName ?? '').trim().length > 0;
    return !hasType && !hasClient;
  });

  /**
   * Returns the display value for a field, truncating at MAX_FIELD_LENGTH unless
   * the user has expanded it. Callers must pass `expandedFields()` so Angular's
   * signal tracking picks up the dependency and re-renders on toggle.
   */
  getDisplayValue(value: string, field: string, expanded: Set<string>): string {
    if (value === '-' || value.length <= MAX_FIELD_LENGTH || expanded.has(field)) {
      return value;
    }
    return `${value.slice(0, MAX_FIELD_LENGTH)}…`;
  }

  /** Returns true when a value exceeds the display limit and hasn't been fully expanded. */
  needsShowMore(value: string): boolean {
    return value !== '-' && value.length > MAX_FIELD_LENGTH;
  }

  /** Toggles the "Show more / Show less" state for a given field key. */
  toggleFieldExpansion(field: string): void {
    this.expandedFields.update(current => {
      const next = new Set(current);
      if (next.has(field)) {
        next.delete(field);
      } else {
        next.add(field);
      }
      return next;
    });
  }

  closePanel(): void {
    this.drawerService.closeAuditDrawer();
    this.close.emit();
  }

  onViewFullLeadHistory(): void {
    const details = this.auditDetails();
    if (!details) {
      return;
    }

    const leadInfoId = String(details.leadInfoId ?? '').trim();
    if (!leadInfoId) return;

    this.appReviewApiService
      .getLeadWithBasicInfo(leadInfoId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: any) => {
        this.dialog.open(LeadDetailView, {
          data: {
            data: {
              ...this.mapAuditLeadToLeadDetailData(details, this.panelData().entityId),
              ...(response?.body?.leadInfo ?? {}),
            },
            leadSourceDD: [],
            viewOnly: true,
            initialTab: 'activities',
          },
        });
      });
  }

  private toDisplayText(value: string | null | undefined): string {
    const normalizedValue = (value ?? '').trim();
    return normalizedValue.length > 0 ? normalizedValue : '-';
  }

  private toDisplayDays(value: number | null | undefined): string {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      return '-';
    }

    return `${value} day${value === 1 ? '' : 's'}`;
  }

  private mapAuditLeadToLeadDetailData(row: AuditDetails, leadId: string): Record<string, unknown> {
    const auditRecordId = String(row.id ?? '').trim() || leadId;

    return {
      id: auditRecordId,
      lead_Id: leadId,
      auditEntityId: auditRecordId,
      clientName: row.clientName || '-',
      lead_Status: '-',
      lead_Stage_Name: '-',
      sourceName: '-',
      source_Of_Lead_Id: '',
      ownerAgentName: '-',
      insuranceCategoryName: '-',
      priority: '',
      priorityName: '',
      lastActivityText: '-',
      lastActivityOn: null,
      created_On: null,
      quoteId: null,
      quoteVersion: null,
      firstName: null,
      lastName: null,
    };
  }
}
